/**
 * SESSION 147a — SmartFlow Phase 4 ML Panel
 *
 * Trains a LightGBM walk-forward predictor on the persisted
 * microstructure data, evaluates OOS edge (AUC, top-K lift), and lets
 * the user score TODAY's universe with one click.
 *
 * The rule-based composite score is fed in as a feature so the model
 * decides whether to lean on it or learn a better blend.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Loader2, Play, RefreshCw, BarChart3, Brain, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date(Date.now() + 330 * 60000).toISOString().split('T')[0];
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const Pill = ({ tone = 'neutral', children, testId }) => {
  const palette = {
    success: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    warn: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    danger: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
    info: 'bg-sky-500/15 text-sky-300 ring-sky-500/30',
    neutral: 'bg-zinc-500/15 text-zinc-300 ring-zinc-500/30',
    accent: 'bg-violet-500/15 text-violet-300 ring-violet-500/30',
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${palette[tone] || palette.neutral}`}>
      {children}
    </span>
  );
};

const Stat = ({ label, value, tone = 'neutral', testId }) => {
  const tint = {
    success: 'text-emerald-300',
    warn: 'text-amber-300',
    danger: 'text-rose-300',
    neutral: 'text-zinc-100',
  }[tone];
  return (
    <div className="rounded-md bg-zinc-900/70 border border-zinc-800 px-3 py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm font-mono font-semibold mt-0.5 ${tint}`}>{value}</div>
    </div>
  );
};

const MLPanel = ({ getAuth }) => {
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [periodFrom, setPeriodFrom] = useState(daysAgoISO(720));
  const [periodTo, setPeriodTo] = useState(daysAgoISO(7));
  const [sampleEvery, setSampleEvery] = useState(10);
  const [universeFilter, setUniverseFilter] = useState('all');
  const [targetReturnPct, setTargetReturnPct] = useState(3);
  const [forwardDays, setForwardDays] = useState(5);
  const [trainMin, setTrainMin] = useState(40);
  const [testWin, setTestWin] = useState(10);
  const [eligibleOnly, setEligibleOnly] = useState(false);  // 147d — default OFF (richer signal)
  const [useFiiDii, setUseFiiDii] = useState(false);  // 147h — Phase C
  const [todayPreds, setTodayPreds] = useState(null);
  const [showImportance, setShowImportance] = useState(false);

  // 147h — Phase C: FII/DII data source state
  const [fiiDiiStatus, setFiiDiiStatus] = useState(null);
  const [fiiDiiBusy, setFiiDiiBusy] = useState(false);

  const refreshFiiDii = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/fii-dii/status`, { headers: getAuth() });
      setFiiDiiStatus(r.data || null);
    } catch {
      // silent
    }
  }, [getAuth]);

  useEffect(() => { refreshFiiDii(); }, [refreshFiiDii]);

  const fiiDiiIngest = async () => {
    try {
      setFiiDiiBusy(true);
      const r = await axios.post(`${API}/api/admin/fii-dii/ingest-today`, {}, { headers: getAuth() });
      toast.success(r.data?.message || 'NSE ingest started.');
      setTimeout(refreshFiiDii, 4000);
    } catch (e) {
      toast.error(`FII/DII: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFiiDiiBusy(false);
    }
  };

  const fiiDiiBackfill = async () => {
    try {
      setFiiDiiBusy(true);
      const r = await axios.post(
        `${API}/api/admin/fii-dii/backfill`,
        { max_pages: 60 },
        { headers: getAuth() },
      );
      toast.success(r.data?.message || 'Historical backfill started.');
      setTimeout(refreshFiiDii, 5000);
    } catch (e) {
      toast.error(`Backfill: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFiiDiiBusy(false);
    }
  };

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/api/admin/smartflow/ml/status`, { headers: getAuth() });
      setLatest(r.data?.latest || null);
      setRunning(!!r.data?.is_running);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [getAuth]);

  useEffect(() => { refresh(); }, [refresh]);

  const trigger = async () => {
    try {
      setRunning(true);
      const r = await axios.post(`${API}/api/admin/smartflow/ml/train`, {
        period_from: periodFrom,
        period_to: periodTo,
        sample_every: Number(sampleEvery),
        universe_filter: universeFilter,
        target_return: Number(targetReturnPct) / 100,
        forward_days: Number(forwardDays),
        train_min_days: Number(trainMin),
        test_window_days: Number(testWin),
        eligible_only: eligibleOnly,
        use_fii_dii: useFiiDii,
      }, { headers: getAuth() });
      toast.success(r.data?.message || 'ML training started.');
      let polled = 0;
      const id = setInterval(async () => {
        polled += 1;
        try {
          const lr = await axios.get(`${API}/api/admin/smartflow/ml/status`, { headers: getAuth() });
          setLatest(lr.data?.latest || null);
          setRunning(!!lr.data?.is_running);
          if (!lr.data?.is_running || polled > 80) clearInterval(id);
        } catch { /* ignore */ }
      }, 15000);
    } catch (e) {
      toast.error(`Trigger: ${e?.response?.data?.detail || e.message}`);
      setRunning(false);
    }
  };

  const fetchTodayPreds = async () => {
    try {
      const r = await axios.get(
        `${API}/api/admin/smartflow/ml/predictions/today?top_n=20&nifty200_only=false`,
        { headers: getAuth() },
      );
      setTodayPreds(r.data || null);
      if (r.data?.status === 'no_model') toast.error('No trained model yet. Run training first.');
    } catch (e) {
      toast.error(`Predict: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const agg = latest?.aggregated || {};
  const importance = latest?.feature_importance || null;
  const sortedImp = importance
    ? Object.entries(importance).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div
      className="rounded-xl border border-violet-700/30 bg-violet-950/15 p-4 space-y-3"
      data-testid="smartflow-ml-panel"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
            <Brain size={14} className="text-violet-300" /> LightGBM Walk-Forward Predictor
            <Pill tone="accent">Phase 4 · ML</Pill>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed max-w-3xl">
            Trains a gradient-boosted classifier to estimate{' '}
            <span className="text-violet-300 font-mono">P(forward return ≥ target)</span>{' '}
            from the same microstructure features the rule-based score uses (delivery %,
            volume ratio, up/down vol, bulk/block, EMAs) plus the rule score itself.
            Walk-forward (no look-ahead): each fold trains on the past, scores the next test window OOS.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="smartflow-ml-refresh"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 items-end">
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          From
          <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-ml-from" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          To
          <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-ml-to" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1" title="Score every Nth trading day. Higher = faster but coarser.">
          Sample every
          <select value={sampleEvery} onChange={(e) => setSampleEvery(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-ml-sample">
            <option value={1}>1 (full · slowest)</option>
            <option value={3}>3</option>
            <option value={5}>5 (recommended)</option>
            <option value={10}>10 (fast)</option>
          </select>
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Universe
          <select value={universeFilter} onChange={(e) => setUniverseFilter(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-ml-universe">
            <option value="all">All real stocks (more samples)</option>
            <option value="nifty200">Nifty 200 (cleaner)</option>
          </select>
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1" title="Stock counts as a winner if forward return ≥ this in `forward_days`.">
          Target return %
          <input type="number" min={0.5} max={20} step={0.5} value={targetReturnPct} onChange={(e) => setTargetReturnPct(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-ml-target" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1">
          Forward days
          <input type="number" min={1} max={30} value={forwardDays} onChange={(e) => setForwardDays(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-ml-forward" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1" title="Min sampling-dates required before the first walk-forward fold.">
          Train min dates
          <input type="number" min={10} max={500} value={trainMin} onChange={(e) => setTrainMin(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-ml-trainmin" />
        </label>
        <label className="text-xs text-zinc-400 flex flex-col gap-1" title="OOS test window per fold, in sampling-dates.">
          Test window
          <input type="number" min={2} max={120} value={testWin} onChange={(e) => setTestWin(e.target.value)} className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-zinc-200 text-xs" data-testid="smartflow-ml-testwin" />
        </label>
        <label className="text-[11px] text-zinc-400 flex items-center gap-2 col-span-2 md:col-span-1" title="Train ONLY on rows that pass production gates (delivery%, EMA20, etc). Phase A research showed setting this OFF gives much higher AUC (0.72 vs 0.56) because the model learns to discriminate winners on the FULL universe instead of just elite stocks.">
          <input type="checkbox" checked={eligibleOnly} onChange={(e) => setEligibleOnly(e.target.checked)} className="accent-violet-500" data-testid="smartflow-ml-eligible-only" />
          <span>Eligible-only training</span>
        </label>
        <label className="text-[11px] text-zinc-400 flex items-center gap-2 col-span-2 md:col-span-1" title="Phase C: append market-wide FII/DII rolling features (1d/5d/20d net flows + divergence + 20d z-score). Requires the FII/DII archive to be backfilled.">
          <input type="checkbox" checked={useFiiDii} onChange={(e) => setUseFiiDii(e.target.checked)} className="accent-emerald-500" data-testid="smartflow-ml-use-fii-dii" />
          <span>Use FII/DII features</span>
        </label>
        <button
          onClick={trigger}
          disabled={running}
          className="rounded-md bg-violet-600 hover:bg-violet-500 px-3 py-2 text-xs text-white font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid="smartflow-ml-train"
        >
          {running ? <Loader2 className="animate-spin" size={12} /> : <Play size={12} />}
          {running ? 'Training…' : 'Train Model'}
        </button>
      </div>

      {/* 147h — FII/DII data source status */}
      <div className="rounded-lg border border-emerald-700/30 bg-emerald-950/15 p-3 space-y-2" data-testid="smartflow-fii-dii-panel">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs font-semibold text-emerald-200 inline-flex items-center gap-1.5">
              <Activity size={12} className="text-emerald-300" /> FII/DII Daily Archive
              <Pill tone="success">Phase C · Market flows</Pill>
            </div>
            <p className="text-[11px] text-zinc-400 mt-0.5 max-w-3xl">
              Foreign + Domestic institutional cash-market net activity. Used as 8 market-wide
              features (1d/5d/20d FII &amp; DII, divergence, 20d z-score). NSE = today's
              authoritative; GitHub archive = historical backfill (~60 days).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={refreshFiiDii}
              className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1"
              data-testid="fii-dii-refresh"
            >
              <RefreshCw size={11} /> Refresh
            </button>
            <button
              onClick={fiiDiiIngest}
              disabled={fiiDiiBusy || fiiDiiStatus?.ingest_running}
              className="rounded-md bg-emerald-700/80 hover:bg-emerald-600 px-2.5 py-1.5 text-xs text-white inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="fii-dii-ingest-today"
            >
              {fiiDiiStatus?.ingest_running ? <Loader2 className="animate-spin" size={11} /> : <Play size={11} />}
              Ingest today (NSE)
            </button>
            <button
              onClick={fiiDiiBackfill}
              disabled={fiiDiiBusy || fiiDiiStatus?.backfill_running}
              className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="fii-dii-backfill"
            >
              {fiiDiiStatus?.backfill_running ? <Loader2 className="animate-spin" size={11} /> : <Play size={11} />}
              Backfill history
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Stat label="Rows persisted" value={fiiDiiStatus?.count?.toLocaleString('en-IN') ?? '—'} testId="fii-dii-count" />
          <Stat label="First date" value={fiiDiiStatus?.first_date ?? '—'} testId="fii-dii-first" />
          <Stat label="Last date" value={fiiDiiStatus?.last_date ?? '—'} testId="fii-dii-last" />
          <Stat
            label="Last FII net (Cr)"
            value={fiiDiiStatus?.last_fii_net_cr != null ? `₹${fiiDiiStatus.last_fii_net_cr.toLocaleString('en-IN')}` : '—'}
            tone={fiiDiiStatus?.last_fii_net_cr > 0 ? 'success' : fiiDiiStatus?.last_fii_net_cr < 0 ? 'danger' : 'neutral'}
            testId="fii-dii-last-fii"
          />
          <Stat
            label="Last DII net (Cr)"
            value={fiiDiiStatus?.last_dii_net_cr != null ? `₹${fiiDiiStatus.last_dii_net_cr.toLocaleString('en-IN')}` : '—'}
            tone={fiiDiiStatus?.last_dii_net_cr > 0 ? 'success' : fiiDiiStatus?.last_dii_net_cr < 0 ? 'danger' : 'neutral'}
            testId="fii-dii-last-dii"
          />
        </div>
      </div>

      {/* Latest run summary */}
      {latest ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="smartflow-ml-result">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] text-zinc-500 font-mono">{latest.run_id}</div>
              <div className="text-xs text-zinc-300 mt-0.5">
                {latest.period_from} → {latest.period_to}
                {' · '}target +{((latest.target_return ?? 0) * 100).toFixed(1)}% in {latest.forward_days}d
                {' · '}universe={latest.universe_filter}
                {' · '}sample 1/{latest.sample_every}
              </div>
            </div>
            <Pill tone={latest.status === 'completed' ? 'success' : latest.status === 'running' ? 'info' : 'danger'}>{latest.status}</Pill>
          </div>

          {latest.status === 'running' && latest.build_progress && (
            <div className="text-[11px] text-zinc-400 font-mono">
              Building dataset… {latest.build_progress.dates_seen}/{latest.build_progress.dates_total} dates ·
              {' '}{latest.build_progress.rows_so_far} rows
            </div>
          )}

          {latest.status === 'failed' && (
            <div className="text-rose-300 text-xs font-mono">{latest.error}</div>
          )}

          {latest.status === 'completed' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                <Stat label="Samples" value={latest.n_samples?.toLocaleString('en-IN')} testId="ml-stat-samples" />
                <Stat label="Positives" value={`${latest.n_positives?.toLocaleString('en-IN')} (${((agg.base_rate ?? 0) * 100).toFixed(1)}%)`} testId="ml-stat-pos" />
                <Stat label="Folds" value={agg.n_folds ?? '—'} testId="ml-stat-folds" />
                <Stat label="Mean AUC" value={agg.mean_auc?.toFixed(3) ?? '—'} tone={agg.mean_auc >= 0.55 ? 'success' : agg.mean_auc >= 0.5 ? 'warn' : 'danger'} testId="ml-stat-auc" />
                <Stat label="Mean LogLoss" value={agg.mean_log_loss?.toFixed(3) ?? '—'} testId="ml-stat-loss" />
                <Stat label="Top-K Lift" value={agg.mean_topk_lift ? `${agg.mean_topk_lift.toFixed(2)}×` : '—'} tone={agg.mean_topk_lift >= 1.2 ? 'success' : agg.mean_topk_lift >= 1.0 ? 'warn' : 'danger'} testId="ml-stat-lift" />
              </div>

              {/* Per-fold metrics */}
              {Array.isArray(latest.fold_metrics) && latest.fold_metrics.length > 0 && (
                <div className="rounded border border-zinc-800 overflow-hidden">
                  <div className="px-3 py-1.5 bg-zinc-900 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                    Walk-forward folds (OOS)
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]" data-testid="ml-fold-table">
                      <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider">
                        <tr>
                          <th className="px-3 py-1.5 text-left">Fold</th>
                          <th className="px-3 py-1.5 text-left">Anchor</th>
                          <th className="px-3 py-1.5 text-right">Train</th>
                          <th className="px-3 py-1.5 text-right">Test</th>
                          <th className="px-3 py-1.5 text-right">AUC</th>
                          <th className="px-3 py-1.5 text-right">LogLoss</th>
                          <th className="px-3 py-1.5 text-right">Brier</th>
                          <th className="px-3 py-1.5 text-right">TopK Win</th>
                          <th className="px-3 py-1.5 text-right">Lift</th>
                        </tr>
                      </thead>
                      <tbody>
                        {latest.fold_metrics.map((m) => (
                          <tr key={m.fold_idx} className="border-t border-zinc-800/60">
                            <td className="px-3 py-1.5 font-mono text-zinc-300">{m.fold_idx}</td>
                            <td className="px-3 py-1.5 font-mono text-zinc-400">{m.anchor_date || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{m.n_train}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{m.n_test}</td>
                            <td className={`px-3 py-1.5 text-right font-mono ${(m.auc ?? 0) >= 0.55 ? 'text-emerald-300' : (m.auc ?? 0) >= 0.5 ? 'text-amber-300' : 'text-rose-300'}`}>{m.auc?.toFixed(3) ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{m.log_loss?.toFixed(3) ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{m.brier?.toFixed(3) ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{m.topk_win_rate ? (m.topk_win_rate * 100).toFixed(1) + '%' : '—'}</td>
                            <td className={`px-3 py-1.5 text-right font-mono ${(m.topk_lift ?? 0) >= 1.2 ? 'text-emerald-300' : (m.topk_lift ?? 0) >= 1 ? 'text-amber-300' : 'text-rose-300'}`}>{m.topk_lift ? `${m.topk_lift.toFixed(2)}×` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Feature importance */}
              {sortedImp.length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowImportance(!showImportance)}
                    className="text-xs text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
                    data-testid="smartflow-ml-toggle-importance"
                  >
                    {showImportance ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    Feature importance (gain)
                  </button>
                  {showImportance && (
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="smartflow-ml-importance">
                      {sortedImp.map(([k, v]) => {
                        const max = sortedImp[0][1] || 1;
                        const w = Math.max(2, Math.round((v / max) * 100));
                        return (
                          <div key={k} className="flex items-center gap-2 text-[11px]">
                            <span className="font-mono text-zinc-300 w-44 truncate" title={k}>{k}</span>
                            <div className="flex-1 h-1.5 bg-zinc-800 rounded overflow-hidden">
                              <div className="h-full bg-violet-500/70" style={{ width: `${w}%` }} />
                            </div>
                            <span className="font-mono text-zinc-500 text-right w-14">{v.toLocaleString('en-IN')}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-zinc-700 px-4 py-6 text-center text-sm text-zinc-400">
          No ML runs yet. Defaults: 2y window · sample 1/10 · target +3% / 5d. Click{' '}
          <span className="text-violet-300 font-semibold">Train Model</span>{' '}
          (~2-5 min depending on universe size).
        </div>
      )}

      {/* Today's predictions */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 space-y-2" data-testid="smartflow-ml-today">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[11px] text-zinc-400">
            <BarChart3 size={11} className="inline mr-1 text-violet-300" />
            Live ranking — score the latest universe with the persisted model.
          </div>
          <button
            onClick={fetchTodayPreds}
            className="rounded-md bg-zinc-800 hover:bg-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 inline-flex items-center gap-1"
            data-testid="smartflow-ml-predict-today"
          >
            <Activity size={11} />
            Score today
          </button>
        </div>
        {todayPreds && todayPreds.candidates && todayPreds.candidates.length > 0 ? (
          <div className="overflow-x-auto rounded border border-zinc-800">
            <table className="w-full text-[11px]" data-testid="smartflow-ml-today-table">
              <thead className="bg-zinc-900/80 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-1.5 text-left">#</th>
                  <th className="px-3 py-1.5 text-left">Symbol</th>
                  <th className="px-3 py-1.5 text-right">P(+{((todayPreds.target_return ?? 0) * 100).toFixed(1)}% / {todayPreds.forward_days}d)</th>
                  <th className="px-3 py-1.5 text-right">Rule Score</th>
                  <th className="px-3 py-1.5 text-right">Δ Z</th>
                  <th className="px-3 py-1.5 text-right">Close</th>
                </tr>
              </thead>
              <tbody>
                {todayPreds.candidates.map((c, i) => (
                  <tr key={c.symbol} className="border-t border-zinc-800/60">
                    <td className="px-3 py-1.5 font-mono text-zinc-500">{i + 1}</td>
                    <td className="px-3 py-1.5 font-mono font-semibold text-zinc-100">{c.symbol}</td>
                    <td className={`px-3 py-1.5 text-right font-mono font-semibold ${c.p_up >= 0.4 ? 'text-emerald-300' : c.p_up >= 0.25 ? 'text-amber-300' : 'text-zinc-400'}`}>
                      {(c.p_up * 100).toFixed(1)}%
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">{c.rule_score?.toFixed(1)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-400">{c.delivery_pct_z?.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-300">₹{c.close?.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : todayPreds && todayPreds.status === 'no_model' ? (
          <div className="text-rose-300 text-xs font-mono">{todayPreds.error}</div>
        ) : todayPreds ? (
          <div className="text-zinc-400 text-xs">No eligible candidates on {todayPreds.trade_date}.</div>
        ) : (
          <div className="text-zinc-500 text-xs italic">Click <span className="text-violet-300">Score today</span> to rank the latest universe.</div>
        )}
      </div>
    </div>
  );
};

export default MLPanel;
